import { useEffect, useState } from 'react';
import { api } from './api.ts';
import type { AvatarData } from './components/Avatar.tsx';

/** Ouvre (ou retrouve) la conversation d'une mission ; l'établissement indique l'extra concerné. Renvoie son identifiant. */
export async function openConversation(missionId: string, extraId?: string): Promise<string> {
  return (await api.post<{ id: string }>('/messages/conversations', { missionId, extraId })).id;
}

export type Side = 'EXTRA' | 'ORG' | 'USER' | 'SUPPORT';

export interface ConversationSummary {
  id: string;
  kind: 'MISSION' | 'SUPPORT';
  mission: { id: string; title: string; startsAt: string; status: string } | null;
  counterpart: string;
  avatar: AvatarData | null;
  lastMessage: { body: string; side: Side; urgent: boolean } | null;
  lastMessageAt: string;
  unread: number;
}

export interface ThreadMeta {
  id: string;
  kind: 'MISSION' | 'SUPPORT';
  side: Side;
  mission: { id: string; title: string; startsAt: string; status: string } | null;
  counterpart: string;
  open: boolean;
  closedReason: null | 'BLOCKED' | 'ENDED' | 'ASSIGNED_ELSEWHERE';
  blockedBy: 'EXTRA' | 'ORG' | null;
  dayJ: boolean;
  quickReplies: { code: string; text: string; urgent: boolean }[];
}

export interface ThreadMessage {
  id: string;
  seq: number;
  side: Side;
  fromMySide: boolean;
  mine: boolean;
  author: string;
  body: string;
  masked: boolean;
  urgent: boolean;
  quick: string | null;
  createdAt: string;
}

export const MESSAGE_ERRORS: Record<string, string> = {
  RATE_LIMITED: 'Vous écrivez trop vite : patientez un instant.',
  CONTACT_INFO_NOT_ALLOWED: 'Ce message ne contient que des coordonnées, qui ne peuvent pas être échangées ici. Écrivez votre question directement.',
  CONVERSATION_CLOSED: 'Cette conversation est fermée.',
  URGENT_LIMIT: 'Vous avez atteint le nombre de messages urgents pour aujourd’hui.',
  DAY_J_ONLY: 'Les messages urgents et réponses rapides sont réservés au jour de la mission, une fois celle-ci confirmée.',
  INVALID_BODY: 'Le message doit contenir entre 1 et 2000 caractères.',
  BLOCKED: 'Vous ne pouvez plus échanger avec cet interlocuteur.',
  TOO_MANY_CONVERSATIONS: 'Vous avez ouvert beaucoup de conversations aujourd’hui : réessayez demain.',
};

export const CLOSED_TEXT: Record<string, string> = {
  ASSIGNED_ELSEWHERE: 'Cette mission a été attribuée à un autre extra : la conversation est en lecture seule.',
  ENDED: 'La mission est terminée depuis plus de 14 jours : la conversation est en lecture seule.',
  BLOCKED: 'Cette conversation est bloquée.',
};

const pad = (n: number) => String(n).padStart(2, '0');
export const clock = (iso: string) => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function dayLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Aujourd’hui';
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function relativeTime(iso: string) {
  const d = new Date(iso);
  const minutes = Math.round((Date.now() - d.getTime()) / 60_000);
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  if (d.toDateString() === new Date().toDateString()) return clock(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/** Nombre de messages non lus, rafraîchi toutes les 30 secondes et au retour sur l'onglet. */
export function useUnreadCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      fetch('/api/messages/unread', { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((b) => alive && b && setCount(b.count))
        .catch(() => undefined);
    void load();
    const timer = setInterval(load, 30_000);
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
  return count;
}
